// Agent Directory Data - Fetches from Supabase-cached profiles first, falls back to live backend
import { API_BASE, baseHeaders, authHeaders as defaultAuthHeaders, getAuthHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';
import { tokenManager } from './external-api';
import { Agent } from '../types/agent';
import { loadRuntimeConfig } from '../config/runtime-config';

interface Property {
  id: string;
  title?: string;
  address?: {
    suburb?: string;
    city?: string;
  };
  suburb?: string;
  city?: string;
  propertyType?: string;
  type?: string;
  agentId?: string;
  assignedAgentId?: string;
  agent?: {
    id?: string;
    name?: string;
  };
}

// Convert user to Agent format with property-derived specializations
async function buildAgentProfile(user: any): Promise<Agent> {
  // Log the raw user data for bio/avatar debugging
  console.log(`[buildAgentProfile] ${user.name || user.email}: bio=${(user.bio || '').slice(0, 50) || '(none)'}, avatar=${user.avatar || user.profilePicture || '(none)'}, jobTitle=${user.jobTitle || '(none)'}`);

  // Fetch all properties assigned to this agent
  const properties = await getAgentProperties(user.id || user.email);
  
  // Extract unique suburbs/cities for specializations
  const suburbs = new Set<string>();
  const cities = new Set<string>();
  const propertyTypes = new Set<string>();
  
  properties.forEach(prop => {
    if (prop.address?.suburb || prop.suburb) {
      suburbs.add(prop.address?.suburb || prop.suburb || '');
    }
    if (prop.address?.city || prop.city) {
      cities.add(prop.address?.city || prop.city || '');
    }
    if (prop.propertyType || prop.type) {
      propertyTypes.add(prop.propertyType || prop.type || '');
    }
  });
  
  // Build specializations from suburbs and cities
  const specializations = [
    ...Array.from(suburbs).filter(Boolean),
    ...Array.from(cities).filter(Boolean)
  ].slice(0, 6); // Limit to 6 areas
  
  // Get property types
  const types = Array.from(propertyTypes).filter(Boolean);
  
  // Calculate stats
  const totalListings = properties.length;
  const soldProperties = properties.filter((p: any) => 
    p.status === 'sold' || p.status === 'Sold'
  ).length;
  
  // Build agent object
  return {
    id: user.id || user.email,
    name: user.name || 'Unknown Agent',
    email: user.email || '',
    phone: user.phone || user.phoneNumber || '+27 (011) 862-1790',
    whatsapp: user.whatsapp || user.phone || user.phoneNumber || '+27615483584',
    avatar: user.avatar || user.profilePicture || '',
    bio: user.bio || `${user.name || 'This agent'} is a dedicated property professional specializing in ${specializations[0] || 'various areas'}. With a commitment to excellent service, ${(user.name || 'they').split(' ')[0]} helps clients find their perfect property match.`,
    title: user.title || user.jobTitle || 'Property Consultant',
    specializations: user.specializations?.length > 0 ? user.specializations : (specializations.length > 0 ? specializations : ['Johannesburg']),
    propertyTypes: user.propertyTypes?.length > 0 ? user.propertyTypes : (types.length > 0 ? types : ['Residential', 'Apartments']),
    yearsExperience: user.yearsExperience || calculateYearsExperience(user.joinedDate || user.createdAt),
    languages: user.languages || ['English'],
    achievements: user.achievements || [],
    totalListings,
    soldProperties,
    rating: user.rating ?? 0,
    joinedDate: user.joinedDate ? new Date(user.joinedDate) : user.createdAt ? new Date(user.createdAt) : new Date(),
    status: user.status === 'Active' || user.status === 'active' ? 'active' : 'inactive'
  };
}

function calculateYearsExperience(joinDate?: string | Date): number {
  if (!joinDate) return 0;
  const joined = new Date(joinDate);
  const now = new Date();
  const years = Math.floor((now.getTime() - joined.getTime()) / (1000 * 60 * 60 * 24 * 365));
  return Math.max(0, years);
}

// Get properties assigned to a specific agent
async function getAgentProperties(agentId: string): Promise<Property[]> {
  try {
    const response = await apiFetch(`${API_BASE}${API.listings.list.path}`, {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      const allProperties = data.data?.listings ?? data.data?.properties ?? data.listings ?? data.properties ?? [];
      
      // Filter properties where the agent matches
      return allProperties.filter((prop: Property) => {
        const propAgentId = prop.agentId || prop.assignedAgentId || prop.agent?.id;
        const propAgentEmail = prop.agent?.name?.toLowerCase();
        
        return propAgentId === agentId || 
               propAgentEmail === agentId.toLowerCase() ||
               propAgentId?.includes(agentId) ||
               agentId.includes(propAgentId || '');
      });
    }
  } catch (error) {
    console.log('Could not fetch properties for agent:', agentId);
  }
  
  return [];
}

/**
 * PRIMARY: Fetch agents from Supabase-cached directory.
 * Bio and profile pictures are stored in Supabase (KV + Storage).
 * The rest of the info (name, status, groups) was synced from the backend.
 * No Cognito token needed — works for public visitors.
 */
async function getCachedDirectoryAgents(): Promise<Agent[]> {
  try {
    // Ensure runtime config is loaded before using API_BASE.
    // loadRuntimeConfig() is idempotent — returns cached result if already loaded.
    // This guards against the race condition where AgentDirectory mounts before
    // App.tsx's loadRuntimeConfig().then(updateAPIBase) has completed.
    let base = API_BASE;
    if (!base) {
      const config = await loadRuntimeConfig().catch(() => null);
      base = config?.apiGateway?.baseUrl || API_BASE;
    }
    if (!base) {
      console.warn('[getCachedDirectoryAgents] API_BASE not available — skipping fetch');
      return [];
    }

    console.log('[getCachedDirectoryAgents] Fetching from /agent-directory...');
    const response = await apiFetch(`${base}/agent-directory`, {
      headers: defaultAuthHeaders,
    });

    if (response.ok) {
      const data = await response.json();
      // Response envelope: { success: true, data: { agents: [], total: n } }
      const agents: any[] = data.data?.agents || data.agents || [];
      
      if (agents.length > 0) {
        console.log(`[getCachedDirectoryAgents] Got ${agents.length} agents from ${data.source || 'cache'}`);
        
        // Convert to Agent type
        return agents.map((a: any) => {
          // Extract P24 ID from avatarUrl for Cognito agents with Property24 listings
          // Pattern: https://...s3.amazonaws.com/agents/property24_XXXXXXX/photo.jpg
          const p24Match = (a.avatarUrl || '').match(/agents\/(property24_\d+)/);
          const externalAgentId = a.externalAgentId || (p24Match ? p24Match[1] : undefined);

          return {
            id: a.id || a.agentId || a.email,
            name: a.name || 'Unknown Agent',
            email: a.email || '',
            phone: a.phone || '',
            whatsapp: a.whatsapp || a.phone || '',
            avatar: a.avatar || a.avatarUrl || '',
            bio: a.bio || '',
            title: a.title || 'Property Consultant',
            specializations: a.specializations || [],
            areasServed: a.areasServed || [],
            propertyTypes: a.propertyTypes || [],
            yearsExperience: a.yearsExperience || 0,
            languages: a.languages || ['English'],
            achievements: a.achievements || [],
            totalListings: a.totalListings || a.listingCount || 0,
            soldProperties: a.soldProperties || 0,
            rating: a.rating ?? 0,
            joinedDate: a.joinedDate ? new Date(a.joinedDate) : new Date(),
            externalAgentId,
            // getDirectory only returns CONFIRMED users in the agent group — always active
            status: 'active',
          } as Agent;
        });
      }
    }
  } catch (error) {
    console.warn('[getCachedDirectoryAgents] Failed to fetch cached directory:', error);
  }
  return [];
}

/**
 * FALLBACK: Fetch agents from /admin/users (needs Cognito token for full data).
 * This is the legacy path that merges Cognito + KV + external API data.
 */
async function getRealAgents(): Promise<Agent[]> {
  try {
    // Build auth headers with Cognito token if available
    const reqHeaders: Record<string, string> = {
      ...defaultAuthHeaders,
    };
    
    try {
      const cognitoToken = await tokenManager.getValidToken();
      if (cognitoToken) {
        reqHeaders['X-Cognito-Token'] = `Bearer ${cognitoToken}`;
      }
    } catch {
      // No Cognito token available — will use KV-only data
    }

    const response = await apiFetch(`${API_BASE}${API.admin.getUsers.path}`, {
      headers: reqHeaders,
    });
    
    if (response.ok) {
      const data = await response.json();
      const users = data.data?.users ?? data.users ?? [];
      
      // Filter for agents only
      const agentUsers = users.filter((user: any) => {
        const role = (user.role || '').toLowerCase();
        const groups = user.groups || [];
        const cognitoGroups = user.cognitoGroups || [];
        
        return role === 'agent' ||
               groups.includes('agent') ||
               cognitoGroups.includes('agent');
      });
      
      console.log(`[getRealAgents] Found ${agentUsers.length} agents via /admin/users:`, 
        agentUsers.map((u: any) => u.name).join(', '));
      
      // Build full agent profiles with property data
      const agentProfiles = await Promise.all(
        agentUsers.map(user => buildAgentProfile(user))
      );
      
      // Only return active agents
      return agentProfiles.filter(agent => agent.status === 'active');
    }
  } catch (error) {
    console.error('[getRealAgents] Error fetching from /admin/users:', error);
  }
  
  return [];
}

/**
 * Trigger a sync of agent profiles from the external API to Supabase.
 * Downloads bio -> KV, profile pictures -> Supabase Storage.
 * Returns sync results or null if no token available.
 */
export async function syncAgentProfiles(): Promise<{ synced: number; agents: any[] } | null> {
  try {
    const cognitoToken = await tokenManager.getValidToken();
    if (!cognitoToken) {
      // Silent — this is expected for unauthenticated users
      return null;
    }

    console.log('[syncAgentProfiles] Triggering agent profile sync...');
    const response = await apiFetch(`${API_BASE}${API.agents.sync.path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      console.log(`[syncAgentProfiles] Synced ${data.synced} agent profiles`);
      return data;
    } else {
      const errText = await response.text();
      console.error(`[syncAgentProfiles] Sync failed (${response.status}):`, errText);
    }
  } catch (error) {
    console.error('[syncAgentProfiles] Error:', error);
  }
  return null;
}

// Throttle background syncs to at most once every 10 minutes
let lastBackgroundSync = 0;
const SYNC_THROTTLE_MS = 10 * 60 * 1000; // 10 minutes

function shouldBackgroundSync(): boolean {
  const now = Date.now();
  if (now - lastBackgroundSync < SYNC_THROTTLE_MS) return false;
  lastBackgroundSync = now;
  return true;
}

/**
 * Main entry point: Fetch agents using a two-tier strategy.
 * 1. Try Supabase-cached directory (bio + profile pics from Supabase, no auth needed)
 * 2. Fall back to /admin/users merge (needs Cognito token for full data)
 * 
 * If a Cognito token is available and no cache exists, automatically trigger a sync.
 */
export async function getAgents(): Promise<Agent[]> {
  // 1. Fetch from cached directory (server now merges agent_cache: + user: KV sources)
  const cachedAgents = await getCachedDirectoryAgents();
  
  if (cachedAgents.length > 0) {
    console.log(`[getAgents] Got ${cachedAgents.length} agents from /agent-directory`);
    
    // Check cache quality and trigger background sync if needed
    const withContent = cachedAgents.filter(a => a.bio || a.avatar);
    const cacheIsStale = withContent.length < Math.ceil(cachedAgents.length * 0.3);
    
    if (cacheIsStale) {
      console.log(`[getAgents] Cache is stale (${withContent.length}/${cachedAgents.length} have bio/avatar) — triggering immediate sync`);
      (async () => {
        try {
          const token = await tokenManager.getValidToken();
          if (token) {
            await syncAgentProfiles();
            console.log('[getAgents] Background sync completed — cache should be refreshed on next load');
          }
        } catch {}
      })();
    } else if (shouldBackgroundSync()) {
      (async () => {
        try {
          const token = await tokenManager.getValidToken();
          if (token) {
            await syncAgentProfiles();
          }
        } catch {}
      })();
    }
    
    return cachedAgents;
  }

  // 2. No agents from directory — try /admin/users fallback (needs auth token)
  console.log('[getAgents] No agents from directory, trying /admin/users fallback...');
  try {
    const token = await tokenManager.getValidToken();
    if (token) {
      const realAgents = await getRealAgents();
      if (realAgents.length > 0) {
        console.log(`[getAgents] Got ${realAgents.length} agents from /admin/users fallback`);
        
        // Background: trigger sync so directory cache is populated for next time
        if (shouldBackgroundSync()) {
          (async () => {
            try { await syncAgentProfiles(); } catch {}
          })();
        }
        
        return realAgents;
      }
    }
  } catch {}
  
  console.warn('[getAgents] No agents found. Create agents via Admin Hub or run sync.');
  return [];
}

// Get only active agents (async version)
export async function getActiveAgents(): Promise<Agent[]> {
  return getAgents();
}

// Get only active agents (synchronous version - returns empty array, use async version)
export function getActiveAgentsSync(): Agent[] {
  console.warn('getActiveAgentsSync() is deprecated. Use await getActiveAgents() instead.');
  return [];
}

export async function getAgentById(id: string): Promise<Agent | null> {
  // 1. Try Cognito directory (internal agents with system accounts)
  const agents = await getAgents();
  const found = agents.find(a => a.id === id || a.email === id);
  if (found) return found;

  // 2. Fall back to direct DynamoDB lookup via GET /agents/{id}
  // This resolves P24 agents (property24_XXXXXX) who are not Cognito users
  try {
    const response = await apiFetch(`${API_BASE}/agents/${encodeURIComponent(id)}`, {});
    if (response.ok) {
      const data = await response.json();
      const raw = data.data?.agent ?? data.agent ?? null;
      if (raw) {
        // Map DynamoDB agent record to frontend Agent type
        const phone = (raw.phoneNumbers?.[0]) || '';
        return {
          id: raw.agentId || id,
          name: [raw.firstName, raw.lastName].filter(Boolean).join(' ') || '',
          email: raw.email || raw.emailAddress || '',
          phone,
          whatsapp: phone,
          avatar: raw.profilePicture || '',
          bio: raw.bio || '',
          title: raw.jobTitle || 'Property Consultant',
          specializations: [],
          propertyTypes: [],
          yearsExperience: 0,
          languages: ['English'],
          achievements: [],
          totalListings: 0,
          soldProperties: 0,
          rating: 0,
          joinedDate: new Date(raw.createdAt || Date.now()),
          status: raw.active !== false ? 'active' : 'inactive',
        };
      }
    }
  } catch {
    // Lookup failed — caller will fall back to default contact
  }

  return null;
}

export async function updateAgent(id: string, updates: Partial<Agent>): Promise<Agent | null> {
  try {
    // Update the user record
    const response = await apiFetch(`${API_BASE}${buildPath(API.admin.updateUser.path, { userId: id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    
    if (response.ok) {
      // Rebuild agent profile with updated data
      const userData = await response.json();
      return buildAgentProfile(userData.user);
    }
  } catch (error) {
    console.error('Error updating agent:', error);
  }
  
  return null;
}

// Export empty array for backward compatibility (deprecated)
export const mockAgents: Agent[] = [];
